/**
 * Sensor Optics — GPU post-processing "looks" for the globe.
 *
 * GLSL 수식은 God's Eye View (github.com/bilawalsidhu/gods-eye-view, MIT) 의
 * Cesium PostProcessStage 셰이더를 deck.gl / luma.gl v9 ShaderPass 규약으로
 * 이식한 것. 원본은 Cesium 전용 uniform (colorTexture / v_textureCoordinates) 을
 * 쓰므로 sampler 패스 시그니처에 맞춰 재작성했다.
 *
 * luma.gl sampler 패스 계약:
 *   vec4 <name>_sampleColor(sampler2D texSrc, vec2 texSize, vec2 coordinate)
 */
import type { ShaderPass } from '@luma.gl/shadertools';

export type SensorMode = 'none' | 'flir' | 'nvg' | 'crt' | 'noir' | 'snow';

export interface SensorPreset {
  id: SensorMode;
  label: string;
  /** 짧은 군용 표기 — HUD 상태줄에 표시 */
  code: string;
  hotkey: string;
  /** HUD 액센트 컬러가 센서에 따라 바뀐다 */
  accent: string;
}

export const SENSOR_PRESETS: SensorPreset[] = [
  { id: 'none', label: 'Electro-Optical', code: 'EO',   hotkey: '1', accent: '#21c6db' },
  { id: 'flir', label: 'Thermal FLIR',    code: 'IR',   hotkey: '2', accent: '#ffb72d' },
  { id: 'nvg',  label: 'Night Vision',    code: 'NVG',  hotkey: '3', accent: '#7bffb2' },
  { id: 'crt',  label: 'CRT Terminal',    code: 'CRT',  hotkey: '4', accent: '#8ff7c7' },
  { id: 'noir', label: 'Noir',            code: 'NOIR', hotkey: '5', accent: '#cbd5e1' },
  { id: 'snow', label: 'Whiteout',        code: 'SNW',  hotkey: '6', accent: '#bae6fd' },
];

// ---------------------------------------------------------------------------
// 공용 GLSL 헬퍼 (해시 / value noise / 배럴 왜곡)
// ---------------------------------------------------------------------------

const GLSL_COMMON = /* glsl */ `
float gev_hash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

float gev_valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = gev_hash(i);
  float b = gev_hash(i + vec2(1.0, 0.0));
  float c = gev_hash(i + vec2(0.0, 1.0));
  float d = gev_hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

vec2 gev_barrel(vec2 uv, float strength) {
  vec2 c = uv * 2.0 - 1.0;
  float r2 = dot(c, c);
  c *= 1.0 + r2 * strength * 0.4 + r2 * r2 * strength * 0.15;
  return c * 0.5 + 0.5;
}

float gev_vignette(vec2 uv, float amount) {
  vec2 v = uv * (1.0 - uv);
  return pow(clamp(v.x * v.y * 16.0, 0.0, 1.0), 0.25 + 0.55 * amount);
}

float gev_luma(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }
`;

// ---------------------------------------------------------------------------
// FLIR — 열화상 (White-Hot / Black-Hot + Ironbow "Predator" 팔레트)
// ---------------------------------------------------------------------------

const flirFs = /* glsl */ `
uniform flirUniforms {
  float time;
  float sensitivity;
  float bloom;
  float blackHot;
  float pixelation;
  float ironbow;
} flir;

${GLSL_COMMON}

// black -> deep purple -> magenta -> red -> orange -> yellow -> white
vec3 gev_ironbow(float t) {
  t = clamp(t, 0.0, 1.0);
  vec3 c0 = vec3(0.00, 0.00, 0.00);
  vec3 c1 = vec3(0.13, 0.00, 0.30);
  vec3 c2 = vec3(0.49, 0.00, 0.45);
  vec3 c3 = vec3(0.86, 0.10, 0.18);
  vec3 c4 = vec3(1.00, 0.55, 0.00);
  vec3 c5 = vec3(1.00, 0.91, 0.32);
  vec3 c6 = vec3(1.00, 1.00, 1.00);
  float s = t * 6.0;
  if (s < 1.0) return mix(c0, c1, s);
  if (s < 2.0) return mix(c1, c2, s - 1.0);
  if (s < 3.0) return mix(c2, c3, s - 2.0);
  if (s < 4.0) return mix(c3, c4, s - 3.0);
  if (s < 5.0) return mix(c4, c5, s - 4.0);
  return mix(c5, c6, s - 5.0);
}

vec4 flir_sampleColor(sampler2D texSrc, vec2 texSize, vec2 uv) {
  // ── 센서 해상도 픽셀화 ──────────────────────────────
  vec2 grid = max(texSize / max(flir.pixelation, 1.0), vec2(1.0));
  vec2 puv = (floor(uv * grid) + 0.5) / grid;

  vec3 src = texture(texSrc, puv).rgb;
  float heat = gev_luma(src);

  // ── 온도 매핑 ───────────────────────────────────────
  // Blue Marble 해양은 luma ~0.05 로 매우 어둡다. 감마를 먼저 걸어
  // 저휘도 구간을 펼친 뒤 sensitivity 로 대비를 준다. 이렇게 해야
  // 바다가 전부 검정으로 뭉개지지 않고 해안선이 살아난다.
  float g = pow(clamp(heat, 0.0, 1.0), 0.55);
  float t = clamp((g - 0.42) * (0.7 + flir.sensitivity * 1.1) + 0.46, 0.0, 1.0);

  // ── 핫스팟 블룸: 링 샘플의 최대 휘도를 번지게 ───────
  float hot = 0.0;
  vec2 texel = 1.0 / texSize;
  for (int i = 0; i < 8; i++) {
    float a = float(i) * 0.7853981;
    vec2 off = vec2(cos(a), sin(a)) * texel * 6.0;
    hot = max(hot, gev_luma(texture(texSrc, puv + off).rgb));
  }
  t += max(hot - 0.72, 0.0) * flir.bloom * 1.6;
  t = clamp(t, 0.0, 1.0);

  // ── 온도 밴딩 (적외선 등온선) ───────────────────────
  float band = sin(t * 46.0) * 0.012;
  t = clamp(t + band, 0.0, 1.0);

  // ── 코히런트 센서 노이즈 ────────────────────────────
  float n = gev_valueNoise(uv * texSize * 0.35 + flir.time * 7.0) - 0.5;
  t = clamp(t + n * 0.045, 0.0, 1.0);

  // ── Black-Hot 반전 ──────────────────────────────────
  float tm = mix(t, 1.0 - t, step(0.5, flir.blackHot));

  vec3 mono = vec3(tm);
  vec3 color = mix(mono, gev_ironbow(tm), flir.ironbow);

  // ── 광학 비네팅 ─────────────────────────────────────
  color *= 0.55 + 0.45 * gev_vignette(uv, 0.7);

  return vec4(color, 1.0);
}
`;

export const flirPass: ShaderPass = {
  name: 'flir',
  fs: flirFs,
  uniformTypes: {
    time: 'f32',
    sensitivity: 'f32',
    bloom: 'f32',
    blackHot: 'f32',
    pixelation: 'f32',
    ironbow: 'f32',
  },
  defaultUniforms: {
    time: 0,
    sensitivity: 0.75,
    bloom: 0.65,
    blackHot: 0,
    pixelation: 1.5,
    ironbow: 1,
  },
  passes: [{ sampler: true }],
};

// ---------------------------------------------------------------------------
// NVG — PVS-14 이미지 인텐시파이어 (P43 인광 그린)
// ---------------------------------------------------------------------------

const nvgFs = /* glsl */ `
uniform nvgUniforms {
  float time;
  float gain;
  float bloom;
  float pixelation;
} nvg;

${GLSL_COMMON}

// 광섬유판 허니콤 텍스처
float gev_honeycomb(vec2 uv, vec2 texSize) {
  vec2 p = uv * texSize * 0.09;
  vec2 r = vec2(1.0, 1.732);
  vec2 h = r * 0.5;
  vec2 a = mod(p, r) - h;
  vec2 b = mod(p - h, r) - h;
  vec2 gv = dot(a, a) < dot(b, b) ? a : b;
  float d = max(abs(gv.x), abs(gv.y * 0.577 + abs(gv.x) * 0.5));
  return smoothstep(0.40, 0.46, d);
}

vec4 nvg_sampleColor(sampler2D texSrc, vec2 texSize, vec2 uv) {
  // ── 렌즈 배럴 왜곡 ──────────────────────────────────
  vec2 duv = gev_barrel(uv, 0.35);
  if (duv.x < 0.0 || duv.x > 1.0 || duv.y < 0.0 || duv.y > 1.0) {
    return vec4(0.0, 0.0, 0.0, 1.0);
  }

  // ── 튜브 해상도 픽셀화 ──────────────────────────────
  vec2 grid = max(texSize / max(nvg.pixelation, 1.0), vec2(1.0));
  vec2 puv = (floor(duv * grid) + 0.5) / grid;

  vec3 src = texture(texSrc, puv).rgb;
  float lum = gev_luma(src);

  // ── 오토 게인: 어두운 영역을 강하게 증폭 ────────────
  float amplified = pow(lum, 1.0 / (1.0 + nvg.gain * 1.8));

  // ── 인텐시파이어 블룸 (밝은 소스 주변 헤일로) ───────
  float halo = 0.0;
  vec2 texel = 1.0 / texSize;
  for (int i = 0; i < 10; i++) {
    float a = float(i) * 0.6283185;
    vec2 off = vec2(cos(a), sin(a)) * texel * 9.0;
    halo += gev_luma(texture(texSrc, puv + off).rgb);
  }
  halo /= 10.0;
  amplified += max(halo - 0.55, 0.0) * nvg.bloom * 2.4;

  // ── 신틸레이션 노이즈 (증폭할수록 거칠어진다) ───────
  float scint = gev_valueNoise(duv * texSize * 0.6 + nvg.time * 24.0) - 0.5;
  amplified += scint * (0.05 + nvg.gain * 0.10);
  amplified = clamp(amplified, 0.0, 1.4);

  // ── P43 인광 그린 ───────────────────────────────────
  vec3 color = vec3(0.10, 1.0, 0.36) * amplified;
  color += vec3(0.02, 0.10, 0.04) * amplified * amplified;

  // ── 광섬유판 허니콤 + 원형 비네팅 ───────────────────
  color *= 0.88 + 0.12 * gev_honeycomb(duv, texSize);

  vec2 c = duv * 2.0 - 1.0;
  float tube = 1.0 - smoothstep(0.86, 1.02, length(c));
  color *= tube;

  return vec4(color, 1.0);
}
`;

export const nvgPass: ShaderPass = {
  name: 'nvg',
  fs: nvgFs,
  uniformTypes: { time: 'f32', gain: 'f32', bloom: 'f32', pixelation: 'f32' },
  defaultUniforms: { time: 0, gain: 0.55, bloom: 0.3, pixelation: 2.5 },
  passes: [{ sampler: true }],
};

// ---------------------------------------------------------------------------
// CRT — 브라운관 터미널 (베이어 디더 + 색수차 + 스캔라인 + 글리치)
// ---------------------------------------------------------------------------

const crtFs = /* glsl */ `
uniform crtUniforms {
  float time;
  float pixelation;
  float distortion;
  float instability;
} crt;

${GLSL_COMMON}

float gev_bayer8(vec2 pos) {
  int x = int(mod(pos.x, 8.0));
  int y = int(mod(pos.y, 8.0));
  int a = x ^ y;
  // 8x8 ordered Bayer = reverse6(interleave(y, x^y)) — 배열 인덱싱 없이 비트로 전개
  int v = ((a & 1) << 5) | ((y & 1) << 4)
        | ((a & 2) << 2) | ((y & 2) << 1)
        | ((a & 4) >> 1) | ((y & 4) >> 2);
  return float(v) / 64.0;
}

vec4 crt_sampleColor(sampler2D texSrc, vec2 texSize, vec2 uv) {
  float inst = crt.instability;

  // ── 수평 지터 + 글리치 라인 ─────────────────────────
  float lineSeed = floor(uv.y * texSize.y * 0.5);
  float glitch = step(0.995 - inst * 0.02, gev_hash(vec2(lineSeed, floor(crt.time * 12.0))));
  float jitter = (gev_hash(vec2(floor(crt.time * 30.0), 3.7)) - 0.5) * 0.0018 * inst;
  jitter += glitch * (gev_hash(vec2(lineSeed, crt.time)) - 0.5) * 0.03 * inst;

  // ── 배럴 왜곡 ───────────────────────────────────────
  vec2 duv = gev_barrel(uv + vec2(jitter, 0.0), crt.distortion);
  if (duv.x < 0.0 || duv.x > 1.0 || duv.y < 0.0 || duv.y > 1.0) {
    return vec4(0.0, 0.0, 0.0, 1.0);
  }

  // ── 픽셀화 ──────────────────────────────────────────
  vec2 grid = max(texSize / max(crt.pixelation, 1.0), vec2(1.0));
  vec2 puv = (floor(duv * grid) + 0.5) / grid;

  // ── 색수차 (RGB 분리) ───────────────────────────────
  vec2 ca = (duv - 0.5) * (0.0022 + inst * 0.0030);
  vec3 color = vec3(
    texture(texSrc, puv + ca).r,
    texture(texSrc, puv).g,
    texture(texSrc, puv - ca).b
  );

  // ── 인광 틴트 (앰버-그린 터미널) ────────────────────
  float lum = gev_luma(color);
  color = mix(color, vec3(0.16, 1.0, 0.52) * lum, 0.55);

  // ── 베이어 디더링 ───────────────────────────────────
  color += (gev_bayer8(duv * texSize / max(crt.pixelation, 1.0)) - 0.5) * 0.055;

  // ── 스캔라인 + RGB 셰도우마스크 서브픽셀 ────────────
  float scan = 0.82 + 0.18 * sin(duv.y * texSize.y * 1.85);
  color *= scan;

  float sub = mod(floor(duv.x * texSize.x), 3.0);
  vec3 mask = vec3(sub == 0.0 ? 1.12 : 0.90, sub == 1.0 ? 1.12 : 0.90, sub == 2.0 ? 1.12 : 0.90);
  color *= mask;

  // ── 플리커 ──────────────────────────────────────────
  color *= 1.0 - inst * 0.06 * (0.5 + 0.5 * sin(crt.time * 47.0));

  // ── 글리치 라인 밝기 튀김 ───────────────────────────
  color += glitch * 0.16;

  // ── 비네팅 ──────────────────────────────────────────
  color *= 0.45 + 0.55 * gev_vignette(duv, 0.85);

  return vec4(clamp(color, 0.0, 1.0), 1.0);
}
`;

export const crtPass: ShaderPass = {
  name: 'crt',
  fs: crtFs,
  uniformTypes: { time: 'f32', pixelation: 'f32', distortion: 'f32', instability: 'f32' },
  defaultUniforms: { time: 0, pixelation: 3.0, distortion: 0.35, instability: 0.4 },
  passes: [{ sampler: true }],
};

// ---------------------------------------------------------------------------
// NOIR — 탈채도 + 고대비 + 그레인 + 세피아 (GEV noir.js 직역)
// ---------------------------------------------------------------------------

const noirFs = /* glsl */ `
uniform noirUniforms {
  float time;
  float contrastAmt;
  float grainAmt;
  float vignetteAmt;
} noir;

${GLSL_COMMON}

vec4 noir_sampleColor(sampler2D texSrc, vec2 texSize, vec2 uv) {
  vec3 src = texture(texSrc, uv).rgb;

  vec3 gray = vec3(gev_luma(src));
  float contrast = 1.0 + noir.contrastAmt;
  vec3 c = clamp((gray - 0.5) * contrast + 0.5, 0.0, 1.0);

  float grain = gev_hash(uv * texSize + noir.time * 60.0);
  c += (grain - 0.5) * 0.08 * noir.grainAmt;

  vec3 sepia = vec3(
    dot(c, vec3(0.393, 0.769, 0.189)),
    dot(c, vec3(0.349, 0.686, 0.168)),
    dot(c, vec3(0.272, 0.534, 0.131))
  );
  c = mix(c, sepia, 0.15);
  c *= gev_vignette(uv, noir.vignetteAmt);

  return vec4(clamp(c, 0.0, 1.0), 1.0);
}
`;

export const noirPass: ShaderPass = {
  name: 'noir',
  fs: noirFs,
  uniformTypes: { time: 'f32', contrastAmt: 'f32', grainAmt: 'f32', vignetteAmt: 'f32' },
  defaultUniforms: { time: 0, contrastAmt: 1.2, grainAmt: 0.5, vignetteAmt: 0.5 },
  passes: [{ sampler: true }],
};

// ---------------------------------------------------------------------------
// SNOW — 한랭 색온도 + 강설 파티클 (GEV snow.js 직역)
// ---------------------------------------------------------------------------

const snowFs = /* glsl */ `
uniform snowUniforms {
  float time;
  float density;
  float wind;
} snow;

${GLSL_COMMON}

float gev_snowLayer(vec2 uv, float layer, float time, float wind) {
  float depth = 0.5 + layer * 0.5;
  float speed = 0.4 + layer * 0.3;
  float size = mix(0.010, 0.025, layer);
  float windForce = sin(time * 0.5 + layer * 3.14159) * (0.05 + wind * 0.2);

  vec2 suv = uv * vec2(1.0, 0.5) * (4.0 + layer * 4.0);
  suv.y += time * speed;
  suv.x += time * windForce;

  vec2 cell = floor(suv);
  vec2 f = fract(suv);

  float acc = 0.0;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 nb = vec2(float(x), float(y));
      vec2 pt = vec2(gev_hash(cell + nb), gev_hash(cell + nb + 100.0));
      pt = 0.5 + 0.4 * sin(time * 0.3 + 6.2831 * pt);
      acc += smoothstep(size, 0.0, length(f - nb - pt)) * depth;
    }
  }
  return acc;
}

vec4 snow_sampleColor(sampler2D texSrc, vec2 texSize, vec2 uv) {
  vec3 src = texture(texSrc, uv).rgb;

  vec3 cool = src * vec3(0.85, 0.90, 1.10);
  vec3 c = mix(cool, vec3(gev_luma(cool)), 0.4);
  c = mix(c, vec3(1.0), 0.15);

  float flakes = 0.0;
  flakes += gev_snowLayer(uv, 0.0, snow.time, snow.wind);
  flakes += gev_snowLayer(uv, 0.5, snow.time, snow.wind);
  flakes += gev_snowLayer(uv, 1.0, snow.time, snow.wind);
  c = mix(c, vec3(1.0), clamp(flakes * snow.density, 0.0, 1.0));

  c *= 0.75 + 0.25 * gev_vignette(uv, 0.4);

  return vec4(clamp(c, 0.0, 1.0), 1.0);
}
`;

export const snowPass: ShaderPass = {
  name: 'snow',
  fs: snowFs,
  uniformTypes: { time: 'f32', density: 'f32', wind: 'f32' },
  defaultUniforms: { time: 0, density: 0.6, wind: 0.5 },
  passes: [{ sampler: true }],
};

// ---------------------------------------------------------------------------

export const SENSOR_PASSES: Record<Exclude<SensorMode, 'none'>, ShaderPass> = {
  flir: flirPass,
  nvg: nvgPass,
  crt: crtPass,
  noir: noirPass,
  snow: snowPass,
};

export function sensorAccent(mode: SensorMode): string {
  return SENSOR_PRESETS.find((p) => p.id === mode)?.accent ?? '#21c6db';
}
